import React, { Component } from 'react';
import { BrowserRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/browser';
import { TssCacheProvider } from 'tss-react';

import { CriticalityLevel, SafeComponentPage } from '~/components/core/SafeComponent';

import '@fontsource/roboto'; // Defaults to weight 400

import CmsMain from './components/CmsMain';
import themeOptions from './theme/material-ui-theme';
import { getMuiEmotionCache, getTssEmotionCache } from './theme/style-injection-order';
import { reportReactError } from './Utils';
import addMethodsToYup from './yupInitialization';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error, info) {
    // Display fallback UI
    this.setState({ hasError: true });
    // You can also log the error to an error reporting service
    reportReactError(error, info);
    Sentry.withScope((scope) => {
      scope.setExtras(info);
      scope.setTags({ criticality_level: CriticalityLevel.HIGHEST });
      Sentry.captureException(error);
    });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      // TODO: make something better
      return <h1>Something went wrong.</h1>;
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.element.isRequired,
};

const muiTheme = createTheme(themeOptions);

class App extends Component {
  render() {
    addMethodsToYup();
    return (
      <SafeComponentPage location="top_level_app_component" criticalityLevel={CriticalityLevel.HIGHEST}>
        {/* The two cache providers pin the head order of injected styles to
            build CSS -> MUI Emotion -> tss-react, so app overrides beat the component styles they
            override regardless of render order (see theme/style-injection-order.ts). */}
        <CacheProvider value={getMuiEmotionCache()}>
          <TssCacheProvider value={getTssEmotionCache()}>
            <BrowserRouter>
              <ThemeProvider theme={muiTheme}>
                <CssBaseline />
                <CmsMain />
              </ThemeProvider>
            </BrowserRouter>
          </TssCacheProvider>
        </CacheProvider>
      </SafeComponentPage>
    );
  }
}

export default App;
